import React, { memo, useMemo } from 'react';
import { ASSETS } from '../utils/assets';
import useMockupRotation from '../hooks/useMockupRotation';
import usePageLoadState from '../hooks/usePageLoadState';
import useInViewport from '../hooks/useInViewport';
import ReviewCard from './ReviewCard';
import OptimizedImage from './OptimizedImage';
import '../styles/hero-buttons.css';

const HeroSection: React.FC = memo(() => {
  const { isMockupVisible } = usePageLoadState();
  const { isInViewport, elementRef } = useInViewport({ threshold: 0.2 });
  const { currentMockup, currentReviews, isTransitioning } = useMockupRotation(isMockupVisible, isInViewport);

  // Memoized Styles für bessere Performance
  const heroStyles = useMemo(() => ({
    section: {
      position: 'relative' as const,
      zIndex: 1,
      width: '100vw',
      minHeight: '100vh',
      marginLeft: 'calc(-50vw + 50%)',
      marginRight: 'calc(-50vw + 50%)',
      overflow: 'hidden'
    },
    backgroundPattern: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundImage: `url(${ASSETS.pattern})`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center top',
      backgroundSize: '120% auto',
      opacity: 0.2,
      zIndex: 1,
      pointerEvents: 'none' as const
    },
    blurGradient: {
      position: 'absolute' as const,
      top: '-20%',
      left: '-20%',
      width: '140%',
      height: '140%',
      background: `
        radial-gradient(circle at 30% 20%, rgba(0, 158, 209, 0.01) 0%, transparent 90%),
        radial-gradient(circle at 70% 40%, rgba(25, 89, 154, 0.005) 0%, transparent 90%),
        radial-gradient(circle at 50% 60%, rgba(63, 182, 211, 0.002) 0%, transparent 80%),
        linear-gradient(180deg, rgba(14, 12, 21, 0.01) 0%, rgba(14, 12, 21, 0.03) 100%)
      `,
      filter: 'blur(0.2px)',
      zIndex: 2,
      pointerEvents: 'none' as const
    },
    noiseOverlay: {
      position: 'absolute' as const,
      top: '-20%',
      left: '-20%',
      width: '140%',
      height: '140%',
      background: `
        radial-gradient(circle at 25% 25%, transparent 0%, rgba(0, 158, 209, 0.005) 40%, transparent 90%),
        radial-gradient(circle at 75% 75%, transparent 0%, rgba(25, 89, 154, 0.004) 40%, transparent 90%),
        radial-gradient(circle at 50% 50%, transparent 0%, rgba(63, 182, 211, 0.003) 30%, transparent 80%)
      `,
      opacity: 0.6,
      zIndex: 2,
      pointerEvents: 'none' as const
    },
    softGlow: {
      position: 'absolute' as const,
      top: '5%',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '120%',
      height: '80%',
      background: 'radial-gradient(ellipse at center, rgba(0, 158, 209, 0.02) 0%, transparent 95%)',
      filter: 'blur(40px)',
      zIndex: 3,
      pointerEvents: 'none' as const
    },
    phoneContainer: {
      opacity: !isMockupVisible ? 0 : (isTransitioning ? 0 : 1),
      transform: !isMockupVisible 
        ? 'translate3d(0, 20px, 0) scale(0.95)' 
        : (isTransitioning ? 'translate3d(0, 0, 0) scale(0.9)' : 'translate3d(0, 0, 0) scale(1)'),
      transition: isMockupVisible 
        ? 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
        : 'opacity 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      backfaceVisibility: 'hidden' as const,
      WebkitBackfaceVisibility: 'hidden' as const,
      WebkitFontSmoothing: 'antialiased' as const,
      willChange: 'opacity, transform'
    },
    successWord: {
      position: 'relative' as const,
      display: 'inline-block',
      cursor: 'default',
      transition: 'all 0.3s ease'
    }
  }), [isTransitioning, isMockupVisible]);

  return (
    <>
      <section id="hero" style={heroStyles.section}>
      {/* Background Pattern */}
      <div style={heroStyles.backgroundPattern} />
      
      {/* Light Blur Gradient Overlay */}
      <div style={heroStyles.blurGradient} />
      
      {/* Noise Overlay */}
      <div style={heroStyles.noiseOverlay} />
      
      {/* Additional Soft Glow */}
      <div style={heroStyles.softGlow} />


      {/* Main Content */}
      <div id="content" style={{ position: 'relative', zIndex: 5 }}>
        <h1>
          Digitale Lösungen für <br />
          Deinen Erfolg
        </h1>
        <p style={{ color: '#cad1e9', fontSize: '1.2rem', textAlign: 'center', maxWidth: '600px' }}>
          Von der Konzeption bis zum fertigen Produkt – wir entwickeln 
          maßgeschneiderte Apps und Websites, die begeistern.
        </p>
        
        <div className="hero-buttons-container">
          <button 
            className="hero-btn-primary pulse"
            data-action="contact"
            onClick={() => {
              console.log('Direct click handler - Beratungsgespräch button clicked');
              // Test: direkter Event dispatch
              const event = new CustomEvent('contactButtonClick');
              document.dispatchEvent(event);
            }}
          >
            <span className="btn-content">
              <i className="fas fa-calendar btn-icon"></i>
              Kostenloses Beratungsgespräch
            </span>
          </button>
          <button 
            className="hero-btn-secondary"
            onClick={() => {
              window.scrollBy({
                top: window.innerHeight,
                behavior: 'smooth'
              });
            }}
          >
            <span className="btn-content">
              Mehr erfahren
            </span>
          </button>
        </div>

        <div id="mockupContainer" ref={elementRef}>
          <div 
            id="phone" 
            style={{ position: 'relative' }}
          >
            <div 
              id="phoneContainer"
              style={heroStyles.phoneContainer}
            >
              <OptimizedImage
                src={currentMockup.image}
                alt={`Smartphone mit App-Beispiel ${currentMockup.id}`}
                priority={true}
                loading="eager"
                className="hero-phone-image"
              />
            </div>
            
            {/* Review Cards */}
            <ReviewCard
              review={currentReviews.leftReview}
              id="left-card"
              isTransitioning={isTransitioning}
              isVisible={isMockupVisible}
            />
            <ReviewCard
              review={currentReviews.rightReview}
              id="right-card"
              isTransitioning={isTransitioning}
              isVisible={isMockupVisible}
            />
          </div>
        </div>
      </div>

      {/* Company Trust Section */}
      <h2>Top-Unternehmen setzen auf Stimm.DEV</h2>
    </section>
    </>
  );
});

HeroSection.displayName = 'HeroSection';

export default HeroSection;
